// Weergave-versies van de FunctionMembers-lijst. De pagina die de lijst toont kiest de versie
// in haar tab-configuratie (TabDataTemplate.listView); deze tabel beschrijft per versie hoe een rij
// eruitziet, welke foto erbij hoort en waar een klik naartoe gaat. Het template rendert generiek uit
// de spec — geen afleiding meer uit requestName of de URL.
// Waarden dragen het voorvoegsel van de lijst (zie list-view.ts): uniek over alle lijsten heen.

export const functionMemberListView = {
    home: 'functionMember.home',
    domain: 'functionMember.domain',
    domainUser: 'functionMember.domainUser',
    project: 'functionMember.project',
    projectMember: 'functionMember.projectMember',
    functionAdmin: 'functionMember.functionAdmin',
    functionDomain: 'functionMember.functionDomain',
    functionProject: 'functionMember.functionProject',
} as const;

export type TFunctionMemberListView = (typeof functionMemberListView)[keyof typeof functionMemberListView];

// Vangnet als een tab geen (geldige) listView meegeeft: functie / domain of project / datums.
export const functionMemberListView_default: TFunctionMemberListView = functionMemberListView.functionAdmin;

// Bouwstenen van een rij. De eerste regel is de titel (met inactief-suffix).
export const functionMemberRowPart = {
    // functienaam (DisplayName ?? Name)
    function: 'function',
    // persoon achter de functie: naam (e-mail)
    person: 'person',
    // "project: Domain - Project" of "domain: Domain" — domain nog niet bekend
    context: 'context',
    // "project: Project", niets bij een domain-functie — domain al bekend
    project: 'project',
    // start/end
    dates: 'dates',
} as const;

export type TFunctionMemberRowPart = (typeof functionMemberRowPart)[keyof typeof functionMemberRowPart];

export const functionMemberPhotoSource = {
    // profielfoto van de persoon achter de functie
    person: 'person',
    // eigen lidmaatschap (Home): eigen domeinfoto van de rij, anders foto van domain/project
    membership: 'membership',
    // context van de functie: domeinfoto van de kijker (zelfde domain), anders foto van domain/project
    context: 'context',
    // geen foto: de persoon staat al in de Details-/Compact-sectie van de pagina (DomainUser/ProjectMember)
    none: 'none',
} as const;

export type TFunctionMemberPhotoSource = (typeof functionMemberPhotoSource)[keyof typeof functionMemberPhotoSource];

export const functionMemberClickTarget = {
    // de persoon achter de functie (DomainUser-/ProjectMember-pagina, tab FunctionMembers)
    member: 'member',
    // de Function-pagina, in domain- of project-context van de functie
    function: 'function',
    // de FunctionMember-pagina in project-context
    functionMember: 'functionMember',
    // geen doorklik: een klik op de rij klapt het paneel open/dicht (alle gegevens staan in het paneel)
    none: 'none',
} as const;

export type TFunctionMemberClickTarget = (typeof functionMemberClickTarget)[keyof typeof functionMemberClickTarget];

// Inhoud van het uitklappaneel (ExpansionListComponent).
export const functionMemberPanel = {
    // alleen de eerdere periodes — uitklapbaar zodra die er zijn
    periods: 'periods',
    // de volledige historie (alle periodes, nieuwste eerst; toont ook de huidige status) — uitklapbaar zodra er periodes zijn
    details: 'details',
    // geen paneel: overzichtslijsten voor iedereen (Domain/Project) tonen geen inhoudelijke gegevens
    // (datums, periodes) van aangestelde functies
    none: 'none',
} as const;

export type TFunctionMemberPanel = (typeof functionMemberPanel)[keyof typeof functionMemberPanel];

export interface IFunctionMemberListViewSpec {
    lines: TFunctionMemberRowPart[];
    photo: TFunctionMemberPhotoSource;
    click: TFunctionMemberClickTarget;
    panel: TFunctionMemberPanel;
}

const __part = functionMemberRowPart;
const __photo = functionMemberPhotoSource;
const __click = functionMemberClickTarget;
const __panel = functionMemberPanel;

export const functionMemberListViewSpec: Record<TFunctionMemberListView, IFunctionMemberListViewSpec> = {
    // Eigen functies, domain-overstijgend.
    [functionMemberListView.home]:            { lines: [__part.function, __part.context, __part.dates], photo: __photo.membership, click: __click.member, panel: __panel.periods },
    // Domain-pagina (ook de AdminPanel-tab): de persoon bovenaan, de functie eronder. Geen datums en geen periodes-paneel: gewone
    // gebruikers hoeven geen inhoudelijke gegevens van aangestelde functies te zien.
    [functionMemberListView.domain]:          { lines: [__part.person, __part.function],                photo: __photo.person,     click: __click.member, panel: __panel.none },
    // DomainUser-pagina: persoon en domain zijn bekend. Net als op de ProjectMember-pagina is elke rij dezelfde
    // persoon (foto al in Details/Compact) — geen foto; klik = de Function-pagina in domain- of project-context
    // van de functie, de uitklapknop toont alle gegevens. De project-regel blijft: de lijst bevat ook de
    // project-functies van deze persoon.
    [functionMemberListView.domainUser]:      { lines: [__part.function, __part.project, __part.dates], photo: __photo.none,       click: __click.function,       panel: __panel.details },
    // Project-pagina: zelfde als de Domain-pagina — de persoon bovenaan, de functie eronder.
    [functionMemberListView.project]:         { lines: [__part.person, __part.function],                photo: __photo.person,     click: __click.member, panel: __panel.none },
    // ProjectMember-pagina: de hele lijst hoort bij dit project en elke rij is dezelfde persoon, wiens foto al
    // in Details/Compact staat — dus geen foto; klik = de Function-pagina in project-context, de uitklapknop
    // toont alle gegevens.
    [functionMemberListView.projectMember]:  { lines: [__part.function, __part.dates],                 photo: __photo.none,       click: __click.function,       panel: __panel.details },
    // Function-pagina in AdminPanel (alle domains), in domain- en in project-context. De functie is de pagina
    // zelf, dus de titel is de persoon (displayName van de DomainUser, met e-mail) i.p.v. de functienaam.
    [functionMemberListView.functionAdmin]:   { lines: [__part.person, __part.context, __part.dates],   photo: __photo.person,     click: __click.member, panel: __panel.periods },
    [functionMemberListView.functionDomain]:  { lines: [__part.person, __part.project, __part.dates],   photo: __photo.person,     click: __click.member, panel: __panel.periods },
    [functionMemberListView.functionProject]: { lines: [__part.person, __part.dates],                   photo: __photo.person,     click: __click.member, panel: __panel.periods },
};
